import { createFileRoute } from "@tanstack/react-router";
import { lazy, Suspense, useEffect } from "react";
import { rememberPilot } from "@/components/forms/quick-quote-shared";
import { Header } from "@/components/site/Header";
import { HeroChicago } from "@/components/site/HeroChicago";
import { NorwoodQuiz } from "@/components/site/NorwoodQuiz";
import { ValueProps } from "@/components/site/ValueProps";
import { Footer } from "@/components/site/Footer";
import { WhyChooseCharlotte } from "@/components/site/WhyChooseCharlotte";
import charlotteLogo from "@/assets/restore/restore-advanced-logo.png.asset.json";
import kurtWagner from "@/assets/restore/kurt-wagner.webp.asset.json";
import drHarrisScott from "@/assets/restore/dr-harris-scott.webp.asset.json";
import { doctors } from "@/lib/site-data";
import { safeJsonLd } from "@/lib/structured-data";
import { getCharlotteStructuredData } from "@/lib/structured-data/charlotte";
import { useLocalQuoteLinks } from "@/hooks/use-local-quote-links";

const Results = lazy(() => import("@/components/site/Results").then(m => ({ default: m.Results })));
const Reviews = lazy(() => import("@/components/site/Reviews").then(m => ({ default: m.Reviews })));
const NoShaveFuture = lazy(() => import("@/components/site/NoShaveFuture").then(m => ({ default: m.NoShaveFuture })));
const NoShaveWhy = lazy(() => import("@/components/site/NoShaveWhy").then(m => ({ default: m.NoShaveWhy })));
const Guarantee = lazy(() => import("@/components/site/Guarantee").then(m => ({ default: m.Guarantee })));
const Pricing = lazy(() => import("@/components/site/Pricing").then(m => ({ default: m.Pricing })));
const DoctorBio = lazy(() => import("@/components/site/DoctorBio").then(m => ({ default: m.DoctorBio })));
const Surgeons = lazy(() => import("@/components/site/Surgeons").then(m => ({ default: m.Surgeons })));
const DoctorsGrid = lazy(() => import("@/components/site/DoctorsGrid").then(m => ({ default: m.DoctorsGrid })));
const LocationsMapCharlotte = lazy(() => import("@/components/site/LocationsMapCharlotte").then(m => ({ default: m.LocationsMapCharlotte })));
const Faq = lazy(() => import("@/components/site/Faq").then(m => ({ default: m.Faq })));
const QuoteForm = lazy(() => import("@/components/site/QuoteForm").then(m => ({ default: m.QuoteForm })));

const FUE_VIDEO_URL = "https://d17ehmrb6zqtjv.cloudfront.net/Videos/Videos%20with%20Captions/F.U.E.ExplainedRESTOREHairWhatisFUE.mp4";

export const Route = createFileRoute("/locations/restore-charlotte")({
  head: () => ({
    links: [{ rel: "canonical", href: "https://restorehair.com/locations/restore-charlotte/" }],
    meta: [
      { title: "Charlotte Hair Transplants | RESTORE® Hair — Charlotte, NC" },
      {
        name: "description",
        content:
          "Restore your confidence with the top hair restoration team serving Charlotte and the surrounding North Carolina metro. Over 20,000 procedures.",
      },
      { property: "og:title", content: "Charlotte Hair Transplants | RESTORE® Hair — Charlotte, NC" },
      {
        property: "og:description",
        content:
          "Top hair restoration team trusted by celebrities, athletes & everyday guys. Soft, natural, undetectable results.",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: safeJsonLd({ "@graph": getCharlotteStructuredData() }),
      },
    ],
  }),
  component: Index,
});

function Index() {
  useLocalQuoteLinks();
  // Charlotte pilot: mark this visit so the end of the quick quote funnel
  // shows the Charlotte booking calendar instead of the national one.
  useEffect(() => {
    rememberPilot("charlotte");
  }, []);
  return (
    <div className="min-h-screen bg-background font-sans">
      <Header logoSrc={charlotteLogo.url} />
      <main>
        <HeroChicago
          eyebrow="RESTORE® | Charlotte Hair Transplants"
          subheading="RESTORE your confidence with the #1 hair restoration team in Charlotte"
        />
        <NorwoodQuiz />
        <WhyChooseCharlotte />
        <Suspense fallback={null}>
          <div className="hidden md:block">
            <Results />
          </div>
          <ValueProps
            hideEyebrowMobile
            imageSrc={drHarrisScott.url}
            imageAlt="Dr. James Harris with RESTORE client Scott N."
            videoUrl={FUE_VIDEO_URL}
          />
          <NoShaveFuture />
          <NoShaveWhy dougLeft />
          <Reviews />
          <Guarantee />
          <Pricing
            imageSrc={kurtWagner.url}
            imageAlt="Kurt Wagner, RESTORE Hair Client"
            city="Charlotte"
            hidePlayOverlay
          />
          <DoctorBio />
          <Surgeons eyebrow="RESTORE Charlotte Hair Transplant Doctors" heading={<>A World Renowned <span className="text-foreground">Team</span></>} />
          <DoctorsGrid doctors={doctors.filter((d) => ["Dr. James Harris", "Dr. Broc Pratt"].includes(d.name))} />
          <LocationsMapCharlotte />
          <Faq />
          <QuoteForm />
        </Suspense>
      </main>
      <Footer />
    </div>
  );
}